import React, { ReactElement, ReactNode, useCallback } from 'react';
import {
  Wizard,
  WizardElementsUncloned,
  WizardProps,
  WizardStore,
} from 'src/components/wizard/Wizard';
import { useGlobalComponentsContext } from 'src/contexts/GlobalComponentsContext';
import { useRedirectCallback } from '../useRedirectCallback';

export type WizardOptions = {
  closeOnRouteChange: boolean;
};

export type WrapperComponentProps = {
  Wrapper?: ReactElement<{ children?: ReactNode }>;
  open: boolean;
};

export function createUseWizard<T extends WrapperComponentProps>(
  WrapperComponent: React.ComponentType<WrapperComponentProps>,
  wrapperId: string,
  options: WizardOptions = { closeOnRouteChange: true },
) {
  return ({
    Wrapper: WrapperDefault,
  }: {
    Wrapper?: ReactElement<{ children?: ReactNode }>;
  } = {}) => {
    const { union, remove } = useGlobalComponentsContext();

    const close = useCallback(() => {
      remove(wrapperId);
    }, [remove]);

    useRedirectCallback(options.closeOnRouteChange ? close : undefined);
    return {
      open: <
        TStore extends WizardStore,
        TElements extends WizardElementsUncloned,
      >({
        onClose,
        Wrapper: InnerWrapper = WrapperDefault,
        wrapperProps,
        ...props
      }: Omit<WizardProps<TStore, TElements>, 'Wrapper'> &
        Pick<T, 'Wrapper'> & { wrapperProps?: Partial<T> }) => {
        union(
          wrapperId,
          <Wizard
            Wrapper={
              <WrapperComponent
                open={true}
                Wrapper={InnerWrapper}
                {...(wrapperProps || {})}
              />
            }
            onClose={() => {
              if (!!onClose) {
                onClose();
              }
              remove(wrapperId);
            }}
            {...props}
          />,
        );
      },
      close,
    };
  };
}
